// ── playerShip.js ─────────────────────────────────────────────────────────────
// All logic related to the player ship: manual movement, auto-nav, blaster
// targeting and firing, player bullet updates, shield recharge, and all draw
// calls for the ship, engine trail, and blaster bolts.
//
// Functions here mutate the shared `st` game-state object directly (same pattern
// as enemyLogic.js). React state is only updated for HUD-visible values.
// ─────────────────────────────────────────────────────────────────────────────

// ── Player ship constants ─────────────────────────────────────────────────────

// Speed of player blaster bolts in px/frame
export const BLASTER_BULLET_SPEED = 5.5;

// Frames after last shield hit before recharge begins (~3 s at 60 fps)
export const SHIELD_RECHARGE_DELAY = 180;

// HP the shield recovers per frame while recharging
export const SHIELD_RECHARGE_RATE  = 0.05;


// ── Utility ───────────────────────────────────────────────────────────────────

/**
 * Lerp between two angles along the shortest arc.
 * Prevents the laser from spinning 350° instead of -10°.
 */
export function aLerp(a, b, t) {
  let d = b - a;
  while (d >  Math.PI) d -= Math.PI * 2;
  while (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}


// ── Movement ──────────────────────────────────────────────────────────────────

/**
 * Move the player ship toward the pointer target each frame.
 * Stops when within 4 px of the target or when the pointer is lifted.
 * Also adds engine-trail particles while moving.
 *
 * @param {object}        st           - Shared game state (mutated)
 * @param {number}        eShipSpeed   - Effective speed (base + research bonuses)
 * @param {number}        W            - Canvas width  (for boundary clamping)
 * @param {number}        H            - Canvas height
 * @param {function|null} onShipMoved  - Optional callback reporting cumulative distance moved
 * @param {object|null}   shipStartRef - Ref object { x, y } tracking origin for distance calc
 * @returns {{ shipMoving: boolean, moveAngle: number }}
 */
export function updatePlayerMovement(st, eShipSpeed, W, H, onShipMoved, shipStartRef) {
  let shipMoving = false;
  let moveAngle  = st.laserAngle; // fallback heading when stationary

  if (st.moveTargetX !== null && st.moveTargetY !== null) {
    const dx   = st.moveTargetX - st.shipX;
    const dy   = st.moveTargetY - st.shipY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (dist > 4) {
      // Step toward target
      st.shipX += (dx / dist) * eShipSpeed;
      st.shipY += (dy / dist) * eShipSpeed;
      shipMoving = true;
      moveAngle  = Math.atan2(dy, dx);

      // Report movement distance to void.jsx for tracking purposes
      if (shipStartRef?.current && onShipMoved) {
        const { x: sx, y: sy } = shipStartRef.current;
        onShipMoved(Math.sqrt((st.shipX - sx) ** 2 + (st.shipY - sy) ** 2));
      }

      // Clamp to canvas so the ship can't leave the arena
      st.shipX = Math.max(16, Math.min(W - 16, st.shipX));
      st.shipY = Math.max(16, Math.min(H - 16, st.shipY));

      // Emit engine trail particle behind the ship
      if (Math.random() < 0.4) {
        const ta = moveAngle + Math.PI + (Math.random() - 0.5) * 0.6;
        st.engineTrail.push({
          x:     st.shipX + Math.cos(ta) * 10,
          y:     st.shipY + Math.sin(ta) * 10,
          vx:    Math.cos(ta) * (0.5 + Math.random() * 0.8),
          vy:    Math.sin(ta) * (0.5 + Math.random() * 0.8),
          life:  1,
          decay: 0.03 + Math.random() * 0.02,
          size:  1 + Math.random() * 1.5,
        });
      }
    } else if (!st.pointerDown) {
      // Arrived at destination (and pointer released) — clear target
      st.moveTargetX = null;
      st.moveTargetY = null;
    }
  }

  // Age and remove old trail particles
  for (let i = st.engineTrail.length - 1; i >= 0; i--) {
    const t = st.engineTrail[i];
    t.x += t.vx; t.y += t.vy;
    t.life -= t.decay;
    if (t.life <= 0) st.engineTrail.splice(i, 1);
  }

  return { shipMoving, moveAngle };
}

/**
 * Auto-nav: automatically steer toward the nearest debris piece when enabled.
 * Only active when not in combat (no enemies alive).
 * Returns updated { shipMoving, moveAngle } if it took over movement.
 *
 * @param {object}  st         - Shared game state
 * @param {boolean} autoNav    - Whether auto-nav is toggled on
 * @param {boolean} inCombat   - True on enemy nodes
 * @param {number}  eShipSpeed
 * @param {number}  eRange     - Laser range (ship stops when close enough to shoot)
 * @param {number}  W
 * @param {number}  H
 * @param {boolean} shipMoving - Current movement state (passed through if auto-nav inactive)
 * @param {number}  moveAngle  - Current heading (passed through if auto-nav inactive)
 * @returns {{ shipMoving: boolean, moveAngle: number }}
 */
export function updateAutoNav(st, autoNav, inCombat, eShipSpeed, eRange, W, H, shipMoving, moveAngle) {
  if (!autoNav || st.debris.length === 0 || inCombat) return { shipMoving, moveAngle };

  // Find the closest debris piece
  let nearest = null, nearestDist = Infinity;
  for (const d of st.debris) {
    const dx = d.x - st.shipX, dy = d.y - st.shipY;
    const dd = Math.sqrt(dx * dx + dy * dy);
    if (dd < nearestDist) { nearest = d; nearestDist = dd; }
  }

  // Only move if the debris is outside laser range
  if (nearest && nearestDist > eRange * 0.55) {
    const dx = nearest.x - st.shipX, dy = nearest.y - st.shipY;
    st.shipX += (dx / nearestDist) * eShipSpeed;
    st.shipY += (dy / nearestDist) * eShipSpeed;
    st.shipX = Math.max(16, Math.min(W - 16, st.shipX));
    st.shipY = Math.max(16, Math.min(H - 16, st.shipY));
    shipMoving = true;
    moveAngle  = Math.atan2(dy, dx);

    if (Math.random() < 0.4) {
      const ta = moveAngle + Math.PI + (Math.random() - 0.5) * 0.6;
      st.engineTrail.push({
        x:     st.shipX + Math.cos(ta) * 10,
        y:     st.shipY + Math.sin(ta) * 10,
        vx:    Math.cos(ta) * (0.5 + Math.random() * 0.8),
        vy:    Math.sin(ta) * (0.5 + Math.random() * 0.8),
        life:  1,
        decay: 0.03 + Math.random() * 0.02,
        size:  1 + Math.random() * 1.5,
      });
    }
  }

  // Auto-nav overrides manual pointer target while active
  st.moveTargetX = null;
  st.moveTargetY = null;

  return { shipMoving, moveAngle };
}


// ── Blaster (combat weapon) ───────────────────────────────────────────────────

/**
 * Find the closest enemy as the blaster target and rotate the weapon angle.
 * Returns the target enemy object or null.
 *
 * @param {object}  st       - Shared game state
 * @param {boolean} inCombat - Only targets enemies in combat nodes
 * @param {number}  eRot     - Rotation speed (for angle lerp)
 * @returns {object|null} blasterTarget
 */
export function updateBlasterTarget(st, inCombat, eRot) {
  let blasterTarget     = null;
  let blasterTargetDist = Infinity;

  if (inCombat && st.enemies.length > 0) {
    for (const e of st.enemies) {
      const dx   = e.x - st.shipX;
      const dy   = e.y - st.shipY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < blasterTargetDist) { blasterTarget = e; blasterTargetDist = dist; }
    }
    if (blasterTarget) {
      st.targetAngle = Math.atan2(blasterTarget.y - st.shipY, blasterTarget.x - st.shipX);
    }
  }

  // Rotate laser/blaster angle toward the target using shortest-arc lerp
  st.laserAngle = aLerp(st.laserAngle, st.targetAngle, eRot);

  return blasterTarget;
}

/**
 * Fire the player blaster when the fire timer expires and a target exists.
 * Spawns one burst of `eBlasterCount` bullets spread around the target angle.
 *
 * @param {object}   st               - Shared game state
 * @param {object}   blasterTarget    - Current target enemy (null = don't fire)
 * @param {number}   eBlasterDmg      - Damage per bolt
 * @param {number}   eBlasterFireRate - Frames between bursts (lower = faster)
 * @param {number}   eBlasterCount    - Number of bolts per burst
 * @param {function} addP             - Particle emitter
 */
export function updateBlasterFire(st, blasterTarget, eBlasterDmg, eBlasterFireRate, eBlasterCount, addP, heatCfg) {
  // heatCfg: { max, perShot, coolRate } — if provided, enforces core-heat lockout
  if (heatCfg) {
    if (st.coreHeat == null)        st.coreHeat = 0;
    if (st.coreHeatLocked == null)  st.coreHeatLocked = false;
    // Passive cooling is always active; when fully cooled below 40%, unlock
    st.coreHeat = Math.max(0, st.coreHeat - heatCfg.coolRate);
    if (st.coreHeatLocked && st.coreHeat < heatCfg.max * 0.4) st.coreHeatLocked = false;
  }
  if (blasterTarget && !(heatCfg && st.coreHeatLocked)) {
    st.blasterTimer--;
    if (st.blasterTimer <= 0) {
      st.blasterTimer = eBlasterFireRate;
      if (heatCfg) {
        st.coreHeat = Math.min(heatCfg.max, st.coreHeat + heatCfg.perShot * eBlasterCount);
        if (st.coreHeat >= heatCfg.max) st.coreHeatLocked = true;
      }
      const baseAng   = Math.atan2(blasterTarget.y - st.shipY, blasterTarget.x - st.shipX);

      // Spread multiple bolts evenly around the base angle
      const spreadStep = eBlasterCount > 1 ? 0.18 : 0;
      const halfSpread = ((eBlasterCount - 1) / 2) * spreadStep;

      for (let b = 0; b < eBlasterCount; b++) {
        const bAng = baseAng - halfSpread + b * spreadStep;
        st.playerBullets.push({
          x:    st.shipX + Math.cos(bAng) * 16,
          y:    st.shipY + Math.sin(bAng) * 16,
          vx:   Math.cos(bAng) * BLASTER_BULLET_SPEED,
          vy:   Math.sin(bAng) * BLASTER_BULLET_SPEED,
          dmg:  eBlasterDmg,
          life: 1,
          decay: 0.006,
        });
      }
      addP(
        st.shipX + Math.cos(baseAng) * 16,
        st.shipY + Math.sin(baseAng) * 16,
        "#4488ff", 2
      );
    }
  } else {
    // Keep the timer nearly-primed so the first shot fires immediately
    // when a target comes into range (avoids a full cooldown delay)
    if (st.blasterTimer > 4) st.blasterTimer--;
  }
}

/**
 * Move player bullets, check for enemy hits, and remove expired/OOB bullets.
 *
 * @param {object}   st  - Shared game state
 * @param {number}   W
 * @param {number}   H
 * @param {function} addP
 */
export function updatePlayerBullets(st, W, H, addP) {
  for (let i = st.playerBullets.length - 1; i >= 0; i--) {
    const b = st.playerBullets[i];
    b.x += b.vx;
    b.y += b.vy;
    b.life -= b.decay;

    let hit = false;
    for (const e of st.enemies) {
      const dx   = b.x - e.x;
      const dy   = b.y - e.y;
      // 14 px hit radius — generous to feel responsive on mobile
      if (Math.sqrt(dx * dx + dy * dy) < 14) {
        // Invulnerable targets (Guardian in default state) only take damage
        // while their armor has been stripped by a successful Precision Breach.
        if (e.invulnerable && !(e.armorStripped > 0)) {
          e.hitFlash = 0.2;
          addP(b.x, b.y, "#666", 2);
        } else {
          e.hp -= b.dmg;
          e.hitFlash = 0.5;
          addP(b.x, b.y, "#88aaff", 3);
        }
        hit = true;
        break;
      }
    }

    if (hit || b.life <= 0 || b.x < 0 || b.x > W || b.y < 0 || b.y > H) {
      st.playerBullets.splice(i, 1);
    }
  }
}


// ── Shield ────────────────────────────────────────────────────────────────────

/**
 * Recharge the shield each frame when: the shield max is > 0, the recharge
 * delay has fully elapsed since the last hit, and the shield isn't already full.
 * Also syncs the current shieldMax if research upgrades changed it mid-session.
 *
 * @param {object}   st              - Shared game state
 * @param {number}   eCurShieldMax   - Current max shield (from stats)
 * @param {object}   shieldMaxRef    - Mutable ref — kept in sync for node-reset effect
 * @param {function} setPlayerShield - React setState (triggers HUD re-render)
 */
export function updateShieldRecharge(st, eCurShieldMax, shieldMaxRef, setPlayerShield) {
  // Sync shieldMax ref if research changed it (e.g. level purchased mid-game)
  if (eCurShieldMax !== shieldMaxRef.current) {
    shieldMaxRef.current = eCurShieldMax;
    if (st.shieldHp > eCurShieldMax) st.shieldHp = eCurShieldMax;
  }

  if (eCurShieldMax > 0) {
    if (st.shieldHitTimer > 0) {
      // Still in the post-hit delay window — count down but don't recharge
      st.shieldHitTimer--;
    } else if (st.shieldHp < eCurShieldMax) {
      // Recharge at a fixed rate per frame
      st.shieldHp = Math.min(eCurShieldMax, st.shieldHp + SHIELD_RECHARGE_RATE);
      setPlayerShield(Math.round(st.shieldHp));
    }
  }
}


// ── Drawing ───────────────────────────────────────────────────────────────────

/**
 * Draw the engine trail particles (behind the ship, drawn before the ship itself
 * so the ship body renders on top).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object[]} engineTrail - st.engineTrail array
 */
export function drawEngineTrail(ctx, engineTrail) {
  engineTrail.forEach(t => {
    ctx.globalAlpha = t.life * 0.5;
    ctx.fillStyle   = `rgba(0,140,255,${t.life})`;
    ctx.beginPath(); ctx.arc(t.x, t.y, t.size, 0, Math.PI * 2); ctx.fill();
  });
  ctx.globalAlpha = 1;
}

/**
 * Draw the player ship at its current position and heading.
 * Flickers when invincible (iFrames > 0) or while in the death animation.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object}  st           - Shared game state
 * @param {boolean} dead         - Whether the player is dead
 * @param {number}  shipHeading  - Current facing angle (radians)
 * @param {boolean} shipMoving   - Whether the ship is currently thrusting
 */
export function drawPlayerShip(ctx, st, dead, shipHeading, shipMoving) {
  // Flicker the ship sprite every other frame while dead (death animation)
  if (dead && Math.floor(st.pulse * 8) % 2 !== 0) return;

  ctx.save();
  ctx.translate(st.shipX, st.shipY);

  // Semi-transparent while invincible so the player can tell they were hit
  if (st.iFrames > 0) ctx.globalAlpha = 0.5 + Math.sin(st.pulse * 20) * 0.5;

  // Blue engine glow halo
  const glow = 18 + Math.sin(st.pulse) * 3;
  const sg   = ctx.createRadialGradient(0, 0, 3, 0, 0, glow);
  sg.addColorStop(0, "rgba(0,160,255,0.25)");
  sg.addColorStop(1, "rgba(0,60,180,0)");
  ctx.fillStyle = sg;
  ctx.beginPath(); ctx.arc(0, 0, glow, 0, Math.PI * 2); ctx.fill();

  // Rotate so the nose points in the heading direction
  ctx.rotate(shipHeading + Math.PI / 2);

  // Main hull body
  ctx.fillStyle   = "#162030";
  ctx.strokeStyle = "#4488bb";
  ctx.lineWidth   = 1.2;
  ctx.beginPath();
  ctx.moveTo(0, -14); ctx.lineTo(-9, 7); ctx.lineTo(-5, 12);
  ctx.lineTo(5, 12);  ctx.lineTo(9, 7);
  ctx.closePath(); ctx.fill(); ctx.stroke();

  // Cockpit lens
  ctx.fillStyle   = "#00ccff";
  ctx.shadowColor = "#00ccff"; ctx.shadowBlur = 6;
  ctx.beginPath(); ctx.ellipse(0, -5, 2.5, 4, 0, 0, Math.PI * 2); ctx.fill();
  ctx.shadowBlur = 0;

  // Wing strakes
  ctx.fillStyle   = "#0b1520";
  ctx.strokeStyle = "#336699";
  ctx.lineWidth   = 0.8;
  ctx.beginPath(); ctx.moveTo(-7, 3); ctx.lineTo(-15, 10); ctx.lineTo(-7, 9); ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.moveTo( 7, 3); ctx.lineTo( 15, 10); ctx.lineTo( 7, 9); ctx.closePath(); ctx.fill(); ctx.stroke();

  // Dual engine nozzles — brighter and longer when thrusting
  const engBright = shipMoving
    ? 0.5 + Math.sin(st.pulse * 4) * 0.2
    : 0.2 + Math.sin(st.pulse * 2.5) * 0.08;
  const engLen = shipMoving
    ? 5 + Math.sin(st.pulse * 3) * 3
    : 3 + Math.sin(st.pulse * 2) * 1.5;

  ctx.fillStyle = `rgba(0,140,255,${engBright})`;
  ctx.beginPath(); ctx.ellipse(-3.5, 14, 1.5, engLen, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.ellipse( 3.5, 14, 1.5, engLen, 0, 0, Math.PI * 2); ctx.fill();

  ctx.restore();
  ctx.globalAlpha = 1;
}

/**
 * Draw player blaster bolts (blue energy projectiles).
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {object[]} playerBullets - st.playerBullets array
 */
export function drawPlayerBullets(ctx, playerBullets) {
  for (const b of playerBullets) {
    ctx.save();
    ctx.globalAlpha = b.life;

    // Outer glow
    ctx.fillStyle   = "#4499ff";
    ctx.shadowColor = "#2266ff"; ctx.shadowBlur = 8;
    ctx.beginPath(); ctx.arc(b.x, b.y, 4, 0, Math.PI * 2); ctx.fill();

    // Inner bright core
    ctx.fillStyle = "#ccddff"; ctx.shadowBlur = 3;
    ctx.beginPath(); ctx.arc(b.x, b.y, 1.8, 0, Math.PI * 2); ctx.fill();

    // Tail trail
    ctx.strokeStyle = `rgba(80,160,255,${b.life * 0.6})`;
    ctx.lineWidth   = 2; ctx.shadowBlur = 0;
    ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.lineTo(b.x - b.vx * 6, b.y - b.vy * 6); ctx.stroke();

    ctx.restore();
  }
}
